import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import Markdown from "./Markdown";
import useLocalStorage from "./Labs/useLocalStorage";
import useTheme, { THEME_LABELS, THEMES } from "./Labs/useTheme";
import {
  countWords, diff, finishedSentences, isEditing, REVISION_SIMILARITY, similarity,
} from "./FreeTypeFeedback/text";
import NativeVersion from "./FreeTypeFeedback/NativeVersion";
import SaveToCollection from "./FreeTypeFeedback/SaveToCollection";
import WritingIdea from "./FreeTypeFeedback/WritingIdea";

const STORAGE_PREFIX = "free-type-feedback:";

// Free Type Feedback - write freely in the target language and each sentence is
// corrected as you finish it. See Labs::FreeTypeFeedbackController.
//
// Two chat-like logs: corrections above the textarea (each one can be asked "why?" and
// then followed up on), and a skinny "How do I say..." column on the right.
//
// POC: nothing is saved. Threads live in state and are sent back with every follow-up.

// How long typing has to pause before finished sentences are sent. Long enough that
// "Je suis allé..." or "3.5" doesn't fire mid-thought, short enough to feel live.
const CHECK_DELAY_MS = 900;
const CONTEXT_CHARS = 600;

async function postJSON(url, body) {
  const token = document.querySelector("meta[name=\"csrf-token\"]");
  const response = await fetch(url, {
    body: JSON.stringify(body),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": token ? token.getAttribute("content") : "",
    },
    method: "POST",
  });

  const json = await response.json().catch(() => ({}));
  if (response.status === 401) throw new Error("Sign in to get feedback.");
  if (!response.ok) {
    const error = new Error((json.errors && json.errors[0]) || "Something went wrong. Please try again.");
    // A 429 carries the learner's AI checks, so the page can show why and until when.
    error.status = response.status;
    error.data = json;
    throw error;
  }

  return json;
}

// Keeps a log pinned to its newest entry as entries arrive.
function useAutoScroll(dependency) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [dependency]);

  return ref;
}

let nextId = 1;

// "12 / 100 AI checks used this month", always, faint - the same line grammar shows.
function ChecksUsed({ aiChecks }) {
  if (!aiChecks) return null;

  return (
    <span className="ftf-checks-used">
      {aiChecks.countThisMonth.toLocaleString()} / {aiChecks.maxPerMonth.toLocaleString()} AI checks used this month
    </span>
  );
}

ChecksUsed.propTypes = {
  aiChecks: PropTypes.object,
};

// Under the textarea: "142 words · 12 / 100 AI checks used this month". Words because a
// task is usually "write 250 words"; characters for languages written without spaces.
function WritingStats({ aiChecks, noWordBoundaries, text }) {
  const count = countWords(text, noWordBoundaries);
  const unit = noWordBoundaries ? "character" : "word";

  return (
    <p className="ftf-stats">
      <span className="ftf-word-count">{count.toLocaleString()} {unit}{count === 1 ? "" : "s"}</span>
      {aiChecks && <>{" · "}<ChecksUsed aiChecks={aiChecks} /></>}
    </p>
  );
}

WritingStats.propTypes = {
  aiChecks: PropTypes.object,
  noWordBoundaries: PropTypes.bool,
  text: PropTypes.string.isRequired,
};

// Where the next correction would have appeared: why nothing's being checked, until when,
// and - for free learners - Pro. Writing carries on regardless.
function OutOfChecks({ aiChecks, proChecksPerMonth, proUrl }) {
  const monthly = aiChecks.countThisMonth >= aiChecks.maxPerMonth;
  const days = aiChecks.monthResetsInDays;

  return (
    <div className="ftf-checks-out">
      <div className="ftf-label">AI checks</div>
      <p>
        {monthly
          ? `You've used all ${aiChecks.maxPerMonth.toLocaleString()} of this month's AI checks. ` +
            `They reset in ${days} ${days === 1 ? "day" : "days"}.`
          : "That's today's limit of AI checks. More tomorrow."}
        {" "}Keep writing - it&apos;s still good practice.
      </p>
      {monthly && aiChecks.plan !== "pro" && proUrl && (
        <p>
          <a className="btn btn-success btn-sm" href={proUrl}>Go Pro</a>
          {proChecksPerMonth && (
            <span className="ftf-muted"> Pro includes {proChecksPerMonth.toLocaleString()} AI checks a month.</span>
          )}
        </p>
      )}
    </div>
  );
}

OutOfChecks.propTypes = {
  aiChecks: PropTypes.object.isRequired,
  proChecksPerMonth: PropTypes.number,
  proUrl: PropTypes.string,
};

const EMPTY_USAGE = { cost: 0, inputTokens: 0, lastCost: null, lastTokens: 0, outputTokens: 0, requests: 0 };

// Luna calls cost fractions of a cent, so "$0.00" would say nothing.
const formatCost = (cost) => `$${cost < 0.01 ? cost.toFixed(5) : cost.toFixed(4)}`;

// Development only: what this page has cost since it loaded, under the columns. Failed and timed-out
// requests aren't counted - they don't come back with usage.
function UsageMeter({ usage }) {
  const tokens = usage.inputTokens + usage.outputTokens;

  return (
    <div className="ftf-usage" title={`${usage.inputTokens.toLocaleString()} in / ${usage.outputTokens.toLocaleString()} out`}>
      <strong>{formatCost(usage.cost)}</strong>
      {" · "}
      {tokens.toLocaleString()} tokens
      {" · "}
      {usage.requests} {usage.requests === 1 ? "request" : "requests"}
      {usage.lastCost !== null && <span className="ftf-usage-last">{" · "}last {formatCost(usage.lastCost)}, {usage.lastTokens.toLocaleString()} tokens</span>}
    </div>
  );
}

UsageMeter.propTypes = {
  usage: PropTypes.object.isRequired,
};

function Thread({ entry, onAsk }) {
  const [question, setQuestion] = useState("");

  const submit = (event) => {
    event.preventDefault();
    if (!question.trim() || entry.threadLoading) return;

    onAsk(entry, question.trim());
    setQuestion("");
  };

  return (
    <div className="ftf-thread">
      {entry.thread.map((message, index) => (
        <div className={`ftf-thread-message ftf-thread-${message.role}`} key={index}>
          {message.role === "assistant" ? <Markdown>{message.content}</Markdown> : message.content}
        </div>
      ))}
      {entry.threadLoading && <div className="ftf-thread-message ftf-thread-assistant ftf-muted">Thinking…</div>}
      {entry.threadError && <div className="ftf-error">{entry.threadError}</div>}
      {entry.thread.length > 0 && (
        <form className="ftf-thread-form" onSubmit={submit}>
          <input
            className="form-control input-sm"
            maxLength={500}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask a follow-up…"
            type="text"
            value={question}
          />
          <button className="btn btn-default btn-sm" disabled={!question.trim() || entry.threadLoading} type="submit">
            Ask
          </button>
        </form>
      )}
    </div>
  );
}

Thread.propTypes = {
  entry: PropTypes.object.isRequired,
  onAsk: PropTypes.func.isRequired,
};

// A sentence that's since been fixed collapses to one dim line, so the log reads as the
// current state of what you wrote rather than every draft of it. It stays in place -
// moving it would make the log jump - and expands to the full card, thread and all.
function Correction(props) {
  const { entry } = props;
  const [expanded, setExpanded] = useState(false);

  if (entry.supersededBy && !expanded) {
    return (
      // A div rather than a <button>: the browser's button styling (centred, wrapping,
      // bordered) is what this line must not have, and it leaks through the moment the
      // stylesheet is stale.
      <div
        className="ftf-correction-superseded"
        onClick={() => setExpanded(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded(true);
          }
        }}
        role="button"
        tabIndex={0}
        title="Show this version"
      >
        <span className="ftf-label">Earlier version</span>{" "}
        <span className="ftf-superseded-text">{entry.original}</span>
      </div>
    );
  }

  const collapseLabel = entry.supersededBy ? "Earlier version" : null;

  return (
    <CorrectionCard
      {...props}
      collapseLabel={collapseLabel}
      onCollapse={collapseLabel ? () => setExpanded(false) : null}
    />
  );
}

function CorrectionCard({ collapseLabel, entry, onAsk, onCollapse, onWhy, quickCapture }) {
  const revised = entry.revisionOf ? <><span className="ftf-revised">Revised</span>{" "}</> : null;
  // An expanded earlier version collapses again from the label at its top. Cards without
  // a label get one.
  const collapse = (label) => (
    <button className="ftf-label ftf-collapse" onClick={onCollapse} title="Collapse" type="button">
      {collapseLabel}{label && ` · ${label}`} <span aria-hidden="true">▴</span>
    </button>
  );
  const header = onCollapse ? collapse(null) : null;

  if (entry.status === "pending") {
    return (
      <div className="ftf-correction ftf-correction-pending">
        {header}
        <div className="ftf-original">{entry.original}</div>
        <div className="ftf-muted">{revised}Checking…</div>
      </div>
    );
  }

  if (entry.status === "failed") {
    return (
      <div className="ftf-correction">
        {header}
        <div className="ftf-original">{entry.original}</div>
        <div className="ftf-error">{entry.error}</div>
      </div>
    );
  }

  if (entry.status === "ok") {
    return (
      <div className="ftf-correction ftf-correction-ok">
        {header}
        <span aria-hidden="true" className="ftf-check">✓</span>
        {revised}
        <span className="ftf-original">{entry.original}</span>
        {!entry.supersededBy && (
          <>
            {" "}
            <SaveToCollection cloze={entry.cloze} quickCapture={quickCapture} sentence={entry.original} />
          </>
        )}
      </div>
    );
  }

  const parts = diff(entry.original, entry.suggestion);

  return (
    <div className={`ftf-correction ftf-correction-${entry.status}`}>
      {onCollapse
        ? collapse(entry.status === "error" ? "Correction" : "More natural")
        : <div className="ftf-label">{revised}{entry.status === "error" ? "Correction" : "More natural"}</div>}
      <div className="ftf-original">
        {parts.filter((p) => p.type !== "added").map((p, i) => (
          <span className={p.type === "removed" ? "ftf-removed" : undefined} key={i}>{p.text}</span>
        ))}
      </div>
      <div className="ftf-suggestion">
        {parts.filter((p) => p.type !== "removed").map((p, i) => (
          <span className={p.type === "added" ? "ftf-added" : undefined} key={i}>{p.text}</span>
        ))}
      </div>
      <div className="ftf-correction-actions">
        {entry.thread.length === 0 && (
          <button className="btn btn-default btn-xs" disabled={entry.threadLoading} onClick={() => onWhy(entry)} type="button">
            Why?
          </button>
        )}
      </div>
      {(entry.thread.length > 0 || entry.threadLoading || entry.threadError) && <Thread entry={entry} onAsk={onAsk} />}
    </div>
  );
}

Correction.propTypes = {
  entry: PropTypes.object.isRequired,
  quickCapture: PropTypes.object,
};

CorrectionCard.propTypes = {
  collapseLabel: PropTypes.string,
  entry: PropTypes.object.isRequired,
  onAsk: PropTypes.func.isRequired,
  onCollapse: PropTypes.func,
  onWhy: PropTypes.func.isRequired,
  quickCapture: PropTypes.object,
};

function HowDoISay({ disabled, onInsert, quickCapture, request, translateUrl }) {
  const [phrase, setPhrase] = useState("");
  const [lookups, setLookups] = useState([]);
  const logRef = useAutoScroll(lookups);

  const update = (id, changes) => setLookups((prev) => prev.map((l) => (l.id === id ? { ...l, ...changes } : l)));

  const submit = async (event) => {
    event.preventDefault();
    const text = phrase.trim();
    if (!text) return;

    const id = nextId++;
    setLookups((prev) => [...prev, { id, loading: true, phrase: text }]);
    setPhrase("");

    try {
      update(id, { loading: false, result: await request(translateUrl, { phrase: text }) });
    } catch (error) {
      update(id, { error: error.message, loading: false });
    }
  };

  return (
    <div className="ftf-panel ftf-how">
      <h2 className="ftf-panel-title">How do I say…</h2>
      <div className="ftf-log" ref={logRef}>
        {lookups.length === 0 && <div className="ftf-empty">Stuck on a word? Ask here and keep writing.</div>}
        {lookups.map((lookup) => (
          <div className="ftf-lookup" key={lookup.id}>
            <div className="ftf-bubble-user">{lookup.phrase}</div>
            {lookup.loading && <div className="ftf-muted">Translating…</div>}
            {lookup.error && <div className="ftf-error">{lookup.error}</div>}
            {lookup.result && (
              <div className="ftf-bubble-ai">
                <div className="ftf-translation-row">
                  <button
                    className="ftf-translation"
                    onClick={() => onInsert(lookup.result.translation)}
                    title="Insert into your text"
                    type="button"
                  >
                    {lookup.result.translation}
                  </button>
                  <SaveToCollection phrase={lookup.result.translation} quickCapture={quickCapture} />
                </div>
                {lookup.result.alternatives.map((alternative) => (
                  <button
                    className="ftf-alternative"
                    key={alternative}
                    onClick={() => onInsert(alternative)}
                    title="Insert into your text"
                    type="button"
                  >
                    {alternative}
                  </button>
                ))}
                {lookup.result.note && <div className="ftf-note">{lookup.result.note}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
      <form className="ftf-how-form" onSubmit={submit}>
        <input
          className="form-control"
          disabled={disabled}
          maxLength={200}
          onChange={(event) => setPhrase(event.target.value)}
          placeholder="a word or phrase…"
          type="text"
          value={phrase}
        />
      </form>
    </div>
  );
}

HowDoISay.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onInsert: PropTypes.func.isRequired,
  quickCapture: PropTypes.object,
  request: PropTypes.func.isRequired,
  translateUrl: PropTypes.string.isRequired,
};

export default function FreeTypeFeedback({
  aiChecks: initialAiChecks,
  baseLanguageName,
  correctUrl,
  explainUrl,
  gameSettingsUrl,
  isSignedIn,
  levels,
  noWordBoundaries,
  proChecksPerMonth,
  proUrl,
  promptUrl,
  quickCapture,
  rewriteUrl,
  showUsage,
  signInUrl,
  signUpUrl,
  targetLanguageIso,
  targetLanguageName,
  translateUrl,
}) {
  const [text, setText] = useState("");
  const [entries, setEntries] = useState([]);
  // The textarea's selection, or null when it isn't focused - so clicking away from a
  // sentence you were fixing (onto "Why?", the other column, anywhere) checks it.
  const [selection, setSelection] = useState(null);
  const [ideaOpen, setIdeaOpen] = useState(false);
  // Mounted on first open and kept, so closing and reopening shows the same idea rather
  // than fetching another - New idea is for that.
  const [ideaStarted, setIdeaStarted] = useState(false);
  // "Native speaker version": each click is a new run (a new rewrite of the text as it
  // is then); closing just hides it.
  const [nativeOpen, setNativeOpen] = useState(false);
  const [nativeRun, setNativeRun] = useState(0);
  // Every sentence already sent, so re-running detection over the whole text only sends
  // what's new. Editing a sentence makes it a new sentence, which is what you want.
  const sent = useRef(new Set());
  const textareaRef = useRef(null);
  const logRef = useAutoScroll(entries);
  // check runs from a timer, so it reads entries through a ref rather than closing over
  // whatever they were when the timer was set.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const update = useCallback((id, changes) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...changes } : e)));
  }, []);

  // Running totals for the development-only meter. Responses only carry usage in
  // development, so elsewhere this never changes and nothing renders it.
  const [usage, setUsage] = useState(EMPTY_USAGE);

  // "base" or "target": which of the pairing's languages explanations and notes come
  // back in. Sent with every request; correcting ignores it.
  const [explanationLanguage, setExplanationLanguage] = useLocalStorage(STORAGE_PREFIX, "explanationLanguage", "base");
  const { chooseTheme, systemDark, theme } = useTheme({ gameSettingsUrl, isSignedIn, prefix: STORAGE_PREFIX });

  // The learner's AI checks (docs/ai_checks.md), refreshed from every response.
  const [aiChecks, setAiChecks] = useState(initialAiChecks);
  const outOfChecks = !!aiChecks && aiChecks.allowed === false;

  const request = useCallback(async (url, body) => {
    let json;
    try {
      json = await postJSON(url, {
        ...body,
        explanation_language: explanationLanguage,
        // Outside development the server only returns usage when asked, and only to admins.
        ...(showUsage ? { show_usage: true } : {}),
      });
    } catch (error) {
      if (error.status === 429 && error.data && error.data.aiChecks) setAiChecks(error.data.aiChecks);
      throw error;
    }

    if (json.aiChecks) setAiChecks(json.aiChecks);

    if (json.usage) {
      setUsage((prev) => ({
        cost: prev.cost + json.usage.cost,
        inputTokens: prev.inputTokens + json.usage.input_tokens,
        lastCost: json.usage.cost,
        lastTokens: json.usage.input_tokens + json.usage.output_tokens,
        outputTokens: prev.outputTokens + json.usage.output_tokens,
        requests: prev.requests + 1,
      }));
    }

    return json;
  }, [explanationLanguage, showUsage]);

  // The entry this sentence is a fix of: one whose sentence is no longer anywhere in the
  // text, and that this one most resembles. claimed stops two new sentences from the
  // same check both claiming it, since entriesRef won't have caught up yet.
  const findRevised = useCallback((sentence, fullText, claimed) => {
    let best = null;
    let bestScore = REVISION_SIMILARITY;

    entriesRef.current.forEach((e) => {
      if (e.supersededBy || claimed.has(e.id) || fullText.includes(e.original)) return;

      const score = similarity(e.original, sentence);
      if (score >= bestScore) {
        best = e;
        bestScore = score;
      }
    });

    return best;
  }, []);

  const check = useCallback((fullText, selection) => {
    // Out of checks: writing carries on, but nothing is sent - every request would only
    // be refused.
    if (outOfChecks) return;

    const claimed = new Set();

    finishedSentences(fullText).forEach(({ end, sentence, start }) => {
      if (sent.current.has(sentence) || isEditing({ end, start }, selection, fullText)) return;
      sent.current.add(sentence);

      const id = nextId++;
      const context = fullText.slice(Math.max(0, start - CONTEXT_CHARS), start).trim();
      const revised = findRevised(sentence, fullText, claimed);

      if (revised) {
        claimed.add(revised.id);
        // Gone from the text, so if you change it back, it's checked again.
        sent.current.delete(revised.original);
      }

      setEntries((prev) => [
        ...prev.map((e) => (revised && e.id === revised.id ? { ...e, supersededBy: id } : e)),
        { id, original: sentence, revisionOf: revised && revised.id, status: "pending", thread: [] },
      ]);

      request(correctUrl, { context, sentence })
        .then((result) => update(id, { cloze: result.cloze, status: result.status, suggestion: result.suggestion }))
        .catch((error) => {
          // Let it be retried by editing or re-typing it.
          sent.current.delete(sentence);

          // Out of checks: the card below the log explains, so drop the entry rather than
          // show the same message against every sentence - and un-fold the one it revised.
          // (A 429 without aiChecks is the burst limit, which is shown like any failure.)
          if (error.status === 429 && error.data && error.data.aiChecks) {
            setEntries((prev) => prev
              .filter((e) => e.id !== id)
              .map((e) => (e.supersededBy === id ? { ...e, supersededBy: null } : e)));
            return;
          }

          update(id, { error: error.message, status: "failed" });
        });
    });
  }, [correctUrl, findRevised, outOfChecks, request, update]);

  useEffect(() => {
    if (!isSignedIn) return undefined;

    const timer = setTimeout(() => check(text, selection), CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [check, isSignedIn, selection, text]);

  // Nothing is saved, so ask before a refresh or navigation throws away what's been
  // written. Browsers show their own generic wording; the message is ignored.
  const hasText = text.trim().length > 0;

  useEffect(() => {
    if (!hasText) return undefined;

    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasText]);

  const trackSelection = useCallback((event) => {
    const { selectionEnd, selectionStart } = event.target;
    setSelection((prev) => (
      prev && prev.start === selectionStart && prev.end === selectionEnd ? prev : { end: selectionEnd, start: selectionStart }
    ));
  }, []);

  const ask = useCallback(async (entry, question) => {
    const thread = question ? [...entry.thread, { content: question, role: "user" }] : entry.thread;
    update(entry.id, { thread, threadError: null, threadLoading: true });

    try {
      const { reply } = await request(explainUrl, {
        messages: thread,
        original: entry.original,
        suggestion: entry.suggestion,
      });
      update(entry.id, { thread: [...thread, { content: reply, role: "assistant" }], threadLoading: false });
    } catch (error) {
      update(entry.id, { threadError: error.message, threadLoading: false });
    }
  }, [explainUrl, request, update]);

  const insert = useCallback((phrase) => {
    const el = textareaRef.current;
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const before = text.slice(0, start);
    const spacer = before && !/\s$/.test(before) ? " " : "";

    setText(before + spacer + phrase + text.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      const caret = start + spacer.length + phrase.length;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }, [text]);

  return (
    <div className="ftf">
      <header className="ftf-header">
        <div>
          <h1>Write in {targetLanguageName}</h1>
          <p className="ftf-subtitle">
            Just write. Each sentence is checked when you finish it - end it with . ! ? or press Enter.
            Going back to fix one? It&apos;s rechecked once you move on.
          </p>
        </div>
        <div className="ftf-settings">
          {isSignedIn && (
            <button
              aria-pressed={ideaOpen}
              className="btn btn-default btn-sm ftf-idea-toggle"
              onClick={() => {
                setIdeaStarted(true);
                setIdeaOpen(!ideaOpen);
              }}
              title="Get a writing prompt"
              type="button"
            >
              <i aria-hidden="true" className="fa fa-lightbulb-o" /> Idea
            </button>
          )}
          {baseLanguageName !== targetLanguageName && (
            <label className="ftf-setting" htmlFor="ftf-explanation-language">
              <span>Explanations in</span>{" "}
              <select
                className="form-control input-sm"
                id="ftf-explanation-language"
                onChange={(event) => setExplanationLanguage(event.target.value)}
                value={explanationLanguage}
              >
                <option value="base">{baseLanguageName}</option>
                <option value="target">{targetLanguageName}</option>
              </select>
            </label>
          )}
          {" "}
          <label className="ftf-setting" htmlFor="ftf-theme">
            <span>Theme</span>{" "}
            <select
              className="form-control input-sm"
              id="ftf-theme"
              onChange={(event) => chooseTheme(event.target.value)}
              title={theme === "auto" ? `Following your system (${systemDark ? "dark" : "light"})` : undefined}
              value={theme}
            >
              {THEMES.map((option) => <option key={option} value={option}>{THEME_LABELS[option]}</option>)}
            </select>
          </label>
        </div>
      </header>

      {!isSignedIn && (
        <div className="alert alert-info">
          <a href={signInUrl}>Sign in</a> or <a href={signUpUrl}>create a free account</a> to get feedback as you write.
        </div>
      )}

      {/* Above both columns rather than inside the writing panel, so it pushes the page
          down instead of squeezing the corrections log. */}
      {ideaStarted && (
        <WritingIdea
          baseLanguageName={baseLanguageName}
          levels={levels}
          onClose={() => setIdeaOpen(false)}
          open={ideaOpen}
          promptUrl={promptUrl}
          request={request}
          storagePrefix={STORAGE_PREFIX}
          targetLanguageIso={targetLanguageIso}
        />
      )}

      <div className="ftf-columns">
        <div className="ftf-panel ftf-write">
          <div className="ftf-log" ref={logRef}>
            {entries.length === 0 && (
              <div className="ftf-empty">Corrections and more natural phrasings will appear here as you write.</div>
            )}
            {entries.map((entry) => (
              <Correction
                entry={entry}
                key={entry.id}
                onAsk={ask}
                onWhy={(e) => ask(e, null)}
                quickCapture={quickCapture}
              />
            ))}
            {outOfChecks && <OutOfChecks aiChecks={aiChecks} proChecksPerMonth={proChecksPerMonth} proUrl={proUrl} />}
          </div>
          <textarea
            autoFocus={isSignedIn}
            className="form-control ftf-textarea"
            disabled={!isSignedIn}
            lang={targetLanguageIso}
            onBlur={() => setSelection(null)}
            onChange={(event) => setText(event.target.value)}
            onSelect={trackSelection}
            placeholder={`Write something in ${targetLanguageName}…`}
            ref={textareaRef}
            rows={5}
            // The point is to find out what you got wrong - the browser shouldn't
            // quietly fix it first.
            spellCheck={false}
            value={text}
          />
          <div className="ftf-footer">
            <div className="ftf-footer-row">
              <WritingStats aiChecks={aiChecks} noWordBoundaries={noWordBoundaries} text={text} />
              {isSignedIn && (
                <button
                  className="btn btn-default btn-sm ftf-native-toggle"
                  disabled={!text.trim() || outOfChecks}
                  onClick={() => {
                    setNativeOpen(true);
                    setNativeRun((run) => run + 1);
                  }}
                  title="Rewrite everything you've written the way a native speaker would"
                  type="button"
                >
                  Native speaker version
                </button>
              )}
            </div>
            <p className="ftf-disclaimer">
              Nothing here is saved - leaving or refreshing the page clears it.
              What you write is sent to OpenAI to generate feedback.
            </p>
            {showUsage && <UsageMeter usage={usage} />}
          </div>
        </div>

        <HowDoISay
          disabled={!isSignedIn}
          onInsert={insert}
          quickCapture={quickCapture}
          request={request}
          translateUrl={translateUrl}
        />
      </div>

      {/* Below both columns: it's the whole text, so it gets the full width. */}
      {nativeOpen && nativeRun > 0 && (
        <NativeVersion
          baseLanguageName={baseLanguageName}
          onClose={() => setNativeOpen(false)}
          quickCapture={quickCapture}
          request={request}
          rewriteUrl={rewriteUrl}
          run={nativeRun}
          targetLanguageIso={targetLanguageIso}
          text={text}
        />
      )}
    </div>
  );
}

FreeTypeFeedback.propTypes = {
  aiChecks: PropTypes.object,
  baseLanguageName: PropTypes.string.isRequired,
  correctUrl: PropTypes.string.isRequired,
  explainUrl: PropTypes.string.isRequired,
  gameSettingsUrl: PropTypes.string,
  isSignedIn: PropTypes.bool.isRequired,
  levels: PropTypes.object.isRequired,
  noWordBoundaries: PropTypes.bool,
  proChecksPerMonth: PropTypes.number,
  proUrl: PropTypes.string,
  promptUrl: PropTypes.string.isRequired,
  quickCapture: PropTypes.object,
  rewriteUrl: PropTypes.string.isRequired,
  showUsage: PropTypes.bool,
  signInUrl: PropTypes.string.isRequired,
  signUpUrl: PropTypes.string.isRequired,
  targetLanguageIso: PropTypes.string,
  targetLanguageName: PropTypes.string.isRequired,
  translateUrl: PropTypes.string.isRequired,
};
